import React, { useEffect, useRef, useState } from 'react';
import { Drawer } from 'antd';
import styled from 'styled-components';
import CloseAlerts from 'Components/alerts/assets/svg/CloseAlerts';
import { maxMobile, scrollbar } from 'Utils/utilsStyle';
import { Alert, Article } from 'Types/ArticleTypes';
import { isSimilarArticleScroll, isSimilarArticleScrollItem } from 'Selector/UISelector';
import { selectKPMGFlag } from 'Selector/GlobalSearchContentSelector';
import { selectSLArticleAlerts } from 'Selector/ServiceLinesSelector'
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { scrollToElement } from 'Utils/UtilityFunctions';
import {
  selectAlertDrawerVisible,
  selectSimilarArticleTwo, selectSimilarDrawerVisible,
} from 'Selector/ArticleSector';
import classNames from 'classnames';
import ArticleInsightPanel from './ArticleInsightsPanel';
import ArticleInfo from './ArticleInfo';

const DrawerLayout = styled.div<{ showInsights: boolean }>`
  display: flex;
  height: 100%;

  .left-panel {
    width: ${({ showInsights }) => (showInsights ? '50%' : '100%')};
    transition: all 0.3s ease;
  }

  .right-panel {
    width: 50%;
    @media all and (max-width: ${maxMobile}) {
      width: 100%;
    }
    border-left: 1px solid #e6e6e6;
    box-shadow: -5px 0px 10px rgba(0, 0, 0, 0.1);
    overflow-y: auto;
  }

  @media all and (max-width: ${maxMobile}) {
    transition: all 0.3s ease;
    .left-panel {
      display: ${({ showInsights }) => (showInsights ? 'none' : 'block')};
    }

    .right-panel {
      display: ${({ showInsights }) => (showInsights ? 'block' : 'none')};
      border-left: none;
    }
  }
`;


const ScrollWrapper = styled.div`
  margin: 0px 10px 0 20px;
  padding: 10px 8px 10px 10px;
  height: calc(100vh - 57px); 
  ${scrollbar};
  @media all and (max-width: ${maxMobile}) {
    height: calc(100vh - 90px);
  }
  &.article-insight {
    margin: 0px;
    padding: 0px; 
    @media all and (max-width: ${maxMobile}) {
      height: calc(100vh - 57px);
    }
  }
`;

const CloseButton = styled.span<{ showInsights: boolean }>`
  position: ${({ showInsights }) => (showInsights ? 'absolute' : 'static')};
  left: ${({ showInsights }) => (showInsights ? '542px' : 'auto')};
  @media all and (max-width: ${maxMobile}) {
    display: ${({ showInsights }) => (showInsights ? 'none' : 'inline-block')};
  }
`;

type Props = {
  visible?: boolean;
  articleId?: number;
  title?: string,
  domain?: string,
  dateTimeStamp?: string,
  summary?: string,
  image?: string,
  url?: string,
  alertId?: number
  companyName?: string
  spotlight?: boolean
  articleAlert?: Alert[]
  isSimilarArticle?: boolean
  similarArticles?: Article[]
  onClose: () => void
  similarArticlesCount?: number
  openInsights?: boolean;
};
const defaultProps = {
  visible: false,
  articleId: 0,
  title: '',
  domain: '',
  dateTimeStamp: '',
  summary: '',
  image: '',
  url: '',
  alertId: 0,
  companyName: '',
  spotlight: false,
  articleAlert: [],
  isSimilarArticle: false,
  similarArticles: [],
  similarArticlesCount: 0,
  openInsights: false,
};
const ArticleDrawer = function ArticleDrawer(props: Props) {
  const {
    visible,
    articleId,
    title,
    domain,
    dateTimeStamp,
    summary,
    image,
    url,
    alertId,
    companyName,
    spotlight,
    articleAlert,
    isSimilarArticle,
    onClose,
    similarArticles,
    similarArticlesCount,
    openInsights,
  } = props;

  const scrollRefDrawerOne = useRef<HTMLDivElement>(null);
  const scrollRefDrawerTwo = useRef<HTMLDivElement>(null);
  const isSimilarArticleScrollFlag: boolean = useSelector(isSimilarArticleScroll);
  const isSimilarArticleScrollItemFlag: boolean = useSelector(isSimilarArticleScrollItem);
  const similarArticlesTwo = useSelector(selectSimilarArticleTwo);
  const similarDrawerVisible = useSelector(selectSimilarDrawerVisible);
  const alertDrawerVisible = useSelector(selectAlertDrawerVisible);
  const articleDetailLoading = !articleId;
  const KPMGFlag = useSelector(selectKPMGFlag);
  const location = useLocation();
  const SLAlerts = useSelector(selectSLArticleAlerts);
  const [showInsights, setShowInsights] = useState<boolean>(false);

  useEffect(() => {
    if (openInsights) {
      setShowInsights(true);
    }
  }, [openInsights]);

  const isServiceLinePage = location.pathname.includes('serviceline');

  const alerts = KPMGFlag && isServiceLinePage && articleId ? SLAlerts[articleId] || [] : articleAlert;

  useEffect(() => {
    if (similarArticlesTwo.length === 0
      && !isSimilarArticleScrollItemFlag && similarDrawerVisible) {
      scrollRefDrawerTwo.current?.scrollTo(0, 0);
    }
  }, [similarArticlesTwo]);

  useEffect(() => {
    if (isSimilarArticleScrollFlag && alertDrawerVisible
      && !similarDrawerVisible && scrollRefDrawerOne?.current) {
      setTimeout(() => {
        scrollToElement('tabs-article-id', scrollRefDrawerOne?.current);
      }, 500);
    }
  }, [alertDrawerVisible, isSimilarArticleScrollFlag, scrollRefDrawerOne?.current]);

  useEffect(() => {
    if (isSimilarArticleScrollItemFlag) {
      setTimeout(() => {
        scrollToElement('tabs-article-id-two', scrollRefDrawerTwo?.current);
      }, 500);
    }
  }, [isSimilarArticleScrollItemFlag]);

  useEffect(() => {
    const node = document.getElementsByClassName('article-preview');
    if (similarDrawerVisible && showInsights) {
      setShowInsights(false);
    }
    return similarDrawerVisible ? node[0]?.classList.add('nestedDrawerViewer') : node[0]?.classList.remove('nestedDrawerViewer');
  }, [similarDrawerVisible, showInsights]);

  return (
    <Drawer
      destroyOnClose
      rootClassName={classNames('drawer-alerts people-group-drawer article-preview', { nestedDrawerViewer: false, showInsights: showInsights })}
      placement="right"
      open={visible}
      onClose={onClose}
      width={showInsights ? 1200 : 609}
      extra={(
        <CloseButton onClick={onClose} onKeyPress={onClose} tabIndex={0} role="button" aria-label="close" className="close-alerts" showInsights={showInsights}>
          <CloseAlerts />
        </CloseButton>
      )}
    >
      <DrawerLayout showInsights={showInsights}>
        <div className="left-panel">
          <ScrollWrapper className="detail-drawer-scroll" ref={isSimilarArticle ? scrollRefDrawerTwo : scrollRefDrawerOne} id={isSimilarArticle ? 'articleDrawerScrollTwo' : 'articleDrawerScroll'}>

            <ArticleInfo
              articleId={articleId}
              title={title}
              domain={domain}
              dateTimeStamp={dateTimeStamp}
              summary={summary}
              image={image}
              url={url}
              alertId={alertId}
              companyName={companyName}
              spotlight={spotlight}
              articleAlert={articleAlert}
              isSimilarArticle={isSimilarArticle}
              similarArticles={similarArticles}
              similarArticlesCount={similarArticlesCount}
              articleDetailLoading={articleDetailLoading}
              showInsights={showInsights}
              onToggleInsights={() => { setShowInsights(prev => !prev) }}
            />

          </ScrollWrapper>
        </div>
        {showInsights && (
          <div className="right-panel">
            <ScrollWrapper className="article-insight">
              <ArticleInsightPanel onClose={() => setShowInsights(false)} />
            </ScrollWrapper>
          </div>
        )}
      </DrawerLayout>
    </Drawer>
  );
};

ArticleDrawer.defaultProps = defaultProps;
export default React.memo(ArticleDrawer);
